import React, { useMemo } from 'react';
import {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import TabNavigationStepper from '../../../../Components/Stepper/TabNavigationStepper/TabNavigationStepper';
import ManagerCalendarWrapper from './Calendar/ManagerCalendarWrapper';
import LaboratorySetup from '../LaboratorySetup/LaboratorySetup';
import ManagerFinance from './Finance/ManagerFinance/ManagerFinance';
import WorkplaceWorkersListWrapper from '../WorkplaceWorkers/WorkplaceWorkersList/WorkplaceWorkersListWrapper';
import CaseTypeList from '../CaseType/List/CaseTypeList';
import { useManagerContext } from '../../Services/ManagerContext';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';
import ManagerHealthPlans from './HealthPlans/ManagerHealthPlans/ManagerHealthPlans';
import ManagerBasicData from './BasicData/ManagerBasicData/ManagerBasicData';
import ManagerCertificates from './Certificates/ManagerCertificates/ManagerCertificates';
import { isAllowed } from '../../../../Services/acl.service';
import InvoicingWrapper from '../../../Finance/Components/Invoicing/InvoicingWrapper/InvoicingWrapper';
import useScope from '../../../Scope/Hooks/useScope';
import WorkplaceProviderCode from '../../../Workplace/Components/WorkplaceProviderCode/WorkplaceProviderCode';

const ManagerWrapper: React.FC = () => {
	const { isSuperAdmin, setHealthPlanCode, healthPlanCode, workplaceId } =
		useManagerContext();
	const { isSlovakia } = useScope();

	const canManageWorkplace = isAllowed('manager_workplace');
	const canManageDoctors = isAllowed('manager_doctors');
	const canManageFinance = isAllowed('manager_finance');
	const canManageInvoicing = isAllowed('manager_invoicing');
	const canManageCertificates = isAllowed('manager_certificates');
	const canManageLaboratories = isAllowed('manager_laboratories');

	const stepMeta = useMemo(() => {
		const temp: { name: string; key: string }[] = [];

		if (canManageWorkplace) {
			temp.push({ name: 'component.managerWrapper.calendar', key: 'calendar' });
			temp.push({
				name: 'component.managerWrapper.caseTypes',
				key: 'caseTypes',
			});
		}

		if (canManageDoctors) {
			temp.push({ name: 'component.managerWrapper.workers', key: 'workers' });
		}

		if (canManageFinance) {
			temp.push({ name: 'component.managerWrapper.finance', key: 'finance' });
		}

		if (canManageInvoicing) {
			temp.push({
				name: 'component.managerWrapper.invoicing',
				key: 'invoicing',
			});
		}

		if (canManageWorkplace) {
			temp.push({
				name: 'component.managerWrapper.basicData',
				key: 'basicData',
			});
		}

		if (canManageCertificates) {
			temp.push({
				name: 'component.managerWrapper.certificates',
				key: 'certificates',
			});
		}

		if (canManageLaboratories) {
			temp.push({
				name: 'component.managerWrapper.laboratories',
				key: 'laboratories',
			});
		}

		if (canManageWorkplace) {
			temp.push({
				name: 'component.managerWrapper.healthPlans',
				key: 'healthPlans',
			});
		}

		if (isSlovakia) {
			temp.push({
				name: 'component.managerWrapper.ambulanceCode',
				key: 'ambulanceCode',
			});
		}

		return temp;
	}, [
		canManageWorkplace,
		canManageDoctors,
		canManageFinance,
		canManageInvoicing,
		canManageCertificates,
		canManageLaboratories,
		isSlovakia,
	]);

	const componentMap: Record<
		string,
		(params: NavigationStepperComponentParams) => JSX.Element
	> = {
		calendar: () => <ManagerCalendarWrapper />,
		caseTypes: () => (
			<CaseTypeList workplaceId={workplaceId} healthPlanCode={healthPlanCode} />
		),
		workers: () => (
			<WorkplaceWorkersListWrapper showAllWorkplaces={isSuperAdmin} />
		),
		finance: () => <ManagerFinance />,
		invoicing: () => <InvoicingWrapper isSuperAdmin={isSuperAdmin} />,
		basicData: () => <ManagerBasicData workplaceId={workplaceId} />,
		certificates: () => <ManagerCertificates />,
		laboratories: () => <LaboratorySetup isSuperAdmin={isSuperAdmin} />,
		healthPlans: (params: NavigationStepperComponentParams) => (
			<ManagerHealthPlans
				openCaseTypes={(selectedCode: HealthPlanCode) => {
					setHealthPlanCode(selectedCode);
					params.setActiveStepPath('caseTypes');
				}}
			/>
		),
		ambulanceCode: () => <WorkplaceProviderCode workplaceId={workplaceId} />,
	};

	const steps: NavigationStepperItemType[] = stepMeta.map(({ name, key }) => ({
		name,
		key,
		component: componentMap[key],
	}));

	const handleOnStepChange = (key: string) => {
		if (key !== 'caseTypes') {
			setHealthPlanCode(undefined);
		}
	};

	return (
		<TabNavigationStepper
			steps={steps}
			initialStepPath='calendar'
			fallbackStep='calendar'
			onStepChange={handleOnStepChange}
			canSkip
		/>
	);
};

export default ManagerWrapper;
